import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

/**
 * Side panels beside the document (outline on the left, document agent on the right), ported from
 * Solution Studio. A panel host decides whether its panels sit beside the page or float over it from
 * the part of the host the person can actually see. Folded expand buttons on narrow hosts render
 * through a portal outside every clipping ancestor (see `side-panel-toggle.tsx`), fixed to the
 * viewport or the enclosing dialog/maximised layer, and clear of iOS safe areas.
 */

/** Overlays that own focus and pointer events: a floating button must render inside them. */
const LAYER_SELECTOR = '[role="dialog"], [role="alertdialog"], .document-workspace-maximised';
export const PANEL_TOGGLE_SIZE = 44;
const PANEL_TOGGLE_INSET = 4;

export type PanelSide = 'left' | 'right';

export interface PanelHostPlacement {
  /** Overlay the button renders into, or null for the document body (viewport-fixed). */
  layer: HTMLElement | null;
  /** Visible part of the host in the layer's coordinate space (viewport space without a layer). */
  top: number;
  left: number;
  right: number;
  width: number;
  /** How far the host extends past the visible area on each side, so panels can shift on screen. */
  hiddenLeft: number;
  hiddenRight: number;
}

/** First-render guess before the host is measured; matches the host width threshold on phones. */
export function initiallyCompact(compactBelow: number): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(`(max-width: ${compactBelow - 1}px)`).matches;
}

export function measurePanelHost(host: HTMLElement): PanelHostPlacement {
  const rect = host.getBoundingClientRect();
  const layer = host.parentElement?.closest<HTMLElement>(LAYER_SELECTOR) ?? null;
  const viewWidth = document.documentElement.clientWidth || window.innerWidth;
  const viewHeight = window.innerHeight;
  let minX = 0;
  let maxX = viewWidth;
  let minY = 0;
  let originX = 0;
  let originY = 0;
  if (layer) {
    const box = layer.getBoundingClientRect();
    originX = box.left + layer.clientLeft - layer.scrollLeft;
    originY = box.top + layer.clientTop - layer.scrollTop;
    minX = Math.max(minX, box.left);
    maxX = Math.min(maxX, box.right);
    minY = Math.max(minY, box.top);
  }
  const visibleLeft = Math.min(Math.max(rect.left, minX), maxX);
  const visibleRight = Math.max(visibleLeft, Math.min(rect.right, maxX));
  const visibleTop = Math.min(Math.max(rect.top, minY), Math.max(minY, viewHeight - PANEL_TOGGLE_SIZE));
  return {
    layer,
    top: visibleTop - originY,
    left: visibleLeft - originX,
    right: visibleRight - originX,
    width: visibleRight - visibleLeft,
    hiddenLeft: Math.max(0, visibleLeft - rect.left),
    hiddenRight: Math.max(0, rect.right - visibleRight),
  };
}

const samePlacement = (a: PanelHostPlacement, b: PanelHostPlacement) => a.layer === b.layer && a.top === b.top && a.left === b.left
  && a.right === b.right && a.width === b.width && a.hiddenLeft === b.hiddenLeft && a.hiddenRight === b.hiddenRight;

/** Tracks whether a panel host is narrow and where its visible box is on screen. */
export function usePanelHost(compactBelow: number) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [compact, setCompact] = useState(() => initiallyCompact(compactBelow));
  const [hostWidth, setHostWidth] = useState(0);
  const [placement, setPlacement] = useState<PanelHostPlacement | null>(null);
  useLayoutEffect(() => {
    const host = ref.current;
    if (!host) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const width = host.getBoundingClientRect().width;
      const next = measurePanelHost(host);
      // A host wider than the screen is still a phone layout: decide from the part the person can see.
      if (width > 0) {
        const visible = next.width > 0 ? Math.min(width, next.width) : width;
        setCompact(visible < compactBelow);
        setHostWidth(visible);
      }
      setPlacement(current => current && samePlacement(current, next) ? current : next);
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    update();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    observer?.observe(host);
    const viewport = window.visualViewport;
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, { capture: true, passive: true });
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, { capture: true });
      viewport?.removeEventListener('resize', schedule);
      viewport?.removeEventListener('scroll', schedule);
    };
  }, [compactBelow]);
  return { ref, compact, hostWidth, placement };
}

/** Inline style that keeps a toggle inside the visible host box and clear of iOS safe areas. */
export function panelToggleStyle(side: PanelSide, placement: PanelHostPlacement | null): CSSProperties {
  const safeTop = 'env(safe-area-inset-top, 0px)';
  if (!placement) {
    return side === 'left'
      ? { top: `max(0.75rem, ${safeTop})`, left: 'max(0.75rem, env(safe-area-inset-left, 0px))' }
      : { top: `max(0.75rem, ${safeTop})`, right: 'max(0.75rem, env(safe-area-inset-right, 0px))' };
  }
  const fixed = !placement.layer;
  const y = Math.round(placement.top + PANEL_TOGGLE_INSET);
  const top = fixed ? `max(${y}px, ${safeTop})` : `${y}px`;
  if (side === 'left') {
    const x = Math.round(placement.left + PANEL_TOGGLE_INSET);
    return { top, left: fixed ? `max(${x}px, env(safe-area-inset-left, 0px))` : `${x}px` };
  }
  const x = Math.round(Math.max(placement.left, placement.right - PANEL_TOGGLE_INSET - PANEL_TOGGLE_SIZE));
  return {
    top,
    left: fixed
      ? `min(${x}px, calc(100% - ${PANEL_TOGGLE_SIZE + PANEL_TOGGLE_INSET}px - env(safe-area-inset-right, 0px)))`
      : `${x}px`,
  };
}

/** Collapse button size inside an open panel: 44px touch targets on narrow hosts. */
export const panelCollapseButtonClass = (compact: boolean) => compact ? 'size-11' : 'size-7';
